import {
    Component,
    computed,
    Inject,
    inject,
    signal,
    AfterViewInit,
    OnDestroy,
    ElementRef,
    ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { ActivatedRoute, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { Store } from '@ngrx/store';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import * as PolicyActions from '../store/policy/policy.actions';
import { APP_CONFIG, AppConfig } from '../../app.config';
import { CallerService } from '../caller.service';
import { globalMessages } from '../shared/global-messages';
import { ToastService } from '../shared/toast/toast.service';

@Component({
    selector: 'app-best-match-component',
    standalone: true,
    imports: [CommonModule, MatCardModule, MatIconModule],
    templateUrl: './best-match-component.component.html',
    styleUrl: './best-match-component.component.scss',
})
export class BestMatchComponentComponent implements AfterViewInit, OnDestroy {
    private callerService = inject(CallerService);
    private router = inject(Router);
    private store = inject(Store);
    private route = inject(ActivatedRoute);
    private toastService = inject(ToastService);
    private http = inject(HttpClient);
    profile = this.callerService.dbCallerProfile;
    private el = inject(ElementRef);
    private cdr = inject(ChangeDetectorRef);
    private resizeObserver!: ResizeObserver;

    feedback = signal<'good' | 'bad' | null>(null);
    isCompact = signal(false);
    bmcSections = this.callerService.bmcSections;
    profileMismatch = computed(() => this.callerService.profilesMatch() === false);

    constructor(@Inject(APP_CONFIG) private config: AppConfig) {}

    setFeedback(value: 'good' | 'bad') {
        this.feedback.set(this.feedback() === value ? null : value);
    }

    navigateToPolicy(policyNumber: string) {
        const trimmed = policyNumber?.trim();

        if (/[^0-9]/.test(trimmed)) {
            this.toastService.add({
                type: 'error',
                title: 'Invalid Policy Number',
                message: `Policy ${trimmed} cannot be searched. Only numeric policy numbers are supported.`,
            });
            return;
        }

        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'x-api-key': this.config.phoneSearchApiKey,
        });

        this.http
            .post<any>(
                this.config.phoneSearchApi,
                { requestType: 'policyLookup', payload: { policyNumber: trimmed } },
                { headers }
            )
            .subscribe({
                next: (response) => {
                    const policies: any[] = response?.policies ?? [];
                    const lob = policies[0]?.lob?.toUpperCase() ?? '';

                    if (policies.length === 0 || lob === 'MCST' || lob === 'MCOR') {
                        this.toastService.add({
                            type: 'error',
                            title: 'Policy Not Found',
                            message: `Policy ${trimmed} cannot be searched for this line of business.`,
                        });
                        return;
                    }

                    this.store.dispatch(PolicyActions.loadPolicyData({ policyNumber: trimmed }));
                    this.router.navigate([], {
                        relativeTo: this.route,
                        queryParams: { policyNumber: trimmed },
                        queryParamsHandling: 'merge',
                    });
                },
                error: () => {
                    this.toastService.add({
                        type: 'error',
                        title: 'Error',
                        message: `Unable to validate policy ${trimmed}. Please try again.`,
                    });
                },
            });
    }

    navigateToAMS(producerCode: string) {
        window.open(`${this.config.amsPortalUrl.replace('<producerCode>', producerCode)}`, '_blank');
    }

    copyToClipboard(value: string) {
        navigator.clipboard.writeText(value).catch((err) => {
            console.error('Failed to copy to clipboard:', err);
        });
    }

    ngAfterViewInit() {
        const target = this.el.nativeElement.querySelector('.cardLayout');
        if (!target) return;
        this.resizeObserver = new ResizeObserver((entries) => {
            const width = entries[0].contentRect.width;
            if (width === 0) return;
            const compact = width < globalMessages.compactBreakpoint;
            if (compact !== this.isCompact()) {
                this.isCompact.set(compact);
                this.cdr.markForCheck();
            }
        });
        this.resizeObserver.observe(target);
    }

    ngOnDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }
}
